import { cloneDeep } from 'lodash';
import { ReplaySubject, merge, mergeAll, map, forkJoin } from 'rxjs';
import { LoadingState } from '@grafana/schema';
import { rangeUtil, preProcessPanelData, DataTopic, DataFrameView } from '@grafana/data';
import { getRunRequest, toDataQueryError } from '@grafana/runtime';
import { SceneObjectBase } from '../core/SceneObjectBase.js';
import { sceneGraph } from '../core/sceneGraph/index.js';
import { getDataSource } from '../utils/getDataSource.js';
import { VariableDependencyConfig } from '../variables/VariableDependencyConfig.js';
import { writeSceneLog } from '../utils/writeSceneLog.js';
import { VariableValueRecorder } from '../variables/VariableValueRecorder.js';
import { emptyPanelData } from '../core/SceneDataNode.js';
import { SceneTimeRangeCompare } from '../components/SceneTimeRangeCompare.js';
import { getClosest } from '../core/sceneGraph/utils.js';
import { timeShiftQueryResponseOperator } from './timeShiftQueryResponseOperator.js';
import { filterAnnotations } from './layers/annotations/filterAnnotations.js';
import { getEnrichedDataRequest } from './getEnrichedDataRequest.js';
import { findActiveAdHocFilterSetByUid } from '../variables/adhoc/patchGetAdhocFilters.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
let counter = 100;
function getNextRequestId() {
  return "SQR" + counter++;
}
class SceneQueryRunner extends SceneObjectBase {
  constructor(initialState) {
    super(initialState);
    this._variableValueRecorder = new VariableValueRecorder();
    this._results = new ReplaySubject();
    this._scopedVars = { __sceneObject: { value: this, text: "__sceneObject" } };
    this._variableDependency = new VariableDependencyConfig(this, {
      statePaths: ["queries", "datasource"],
      onVariableUpdatesCompleted: (variables, dependencyChanged) => this.onVariableUpdatesCompleted(variables, dependencyChanged)
    });
    this.prepareRequests = (timeRange, ds) => {
      const comparer = this.getTimeCompare();
      const { minInterval, queries } = this.state;
      let secondaryRequest;
      let request = __spreadValues({
        app: "scenes",
        requestId: getNextRequestId(),
        timezone: timeRange.getTimeZone(),
        panelId: 1,
        range: timeRange.state.value,
        interval: "1s",
        intervalMs: 1e3,
        targets: cloneDeep(queries),
        maxDataPoints: this.getMaxDataPoints(),
        scopedVars: this._scopedVars,
        startTime: Date.now(),
        liveStreaming: this.state.liveStreaming,
        rangeRaw: {
          from: timeRange.state.from,
          to: timeRange.state.to
        }
      }, getEnrichedDataRequest(this));
      if (this._adhocFilterSet) {
        request.filters = this._adhocFilterSet.state.filters;
      }
      request.targets = request.targets.map((query) => {
        if (!query.datasource) {
          query.datasource = ds.getRef();
        }
        return query;
      });
      const lowerIntervalLimit = minInterval ? minInterval : ds.interval;
      const norm = rangeUtil.calculateInterval(timeRange.state.value, request.maxDataPoints, lowerIntervalLimit);
      request.scopedVars = Object.assign({}, request.scopedVars, {
        __interval: { text: norm.interval, value: norm.interval },
        __interval_ms: { text: norm.intervalMs.toString(), value: norm.intervalMs }
      });
      request.interval = norm.interval;
      request.intervalMs = norm.intervalMs;
      const primaryTimeRange = timeRange.state.value;
      if (comparer) {
        const secondaryTimeRange = comparer.getCompareTimeRange(primaryTimeRange);
        if (secondaryTimeRange) {
          secondaryRequest = __spreadProps(__spreadValues({}, request), {
            range: secondaryTimeRange,
            requestId: getNextRequestId()
          });
          request = __spreadProps(__spreadValues({}, request), {
            range: primaryTimeRange
          });
        }
      }
      return [request, secondaryRequest];
    };
    this.onDataReceived = (data) => {
      this._results.next({ origin: this, data });
      const preProcessedData = preProcessPanelData(data, this.state.data);
      const dataWithLayersApplied = this._combineDataLayers(preProcessedData);
      let hasFetchedData = this.state._hasFetchedData;
      if (!hasFetchedData && preProcessedData.state !== LoadingState.Loading) {
        hasFetchedData = true;
      }
      this.setState({ data: dataWithLayersApplied, _hasFetchedData: hasFetchedData });
    };
    this.addActivationHandler(() => this._onActivate());
  }
  getResultsStream() {
    return this._results;
  }
  _onActivate() {
    const timeRange = sceneGraph.getTimeRange(this);
    const comparer = this.getTimeCompare();
    if (comparer) {
      this._subs.add(
        comparer.subscribeToState((n, p) => {
          if (n.compareWith !== p.compareWith) {
            this.runQueries();
          }
        })
      );
    }
    this._subs.add(
      timeRange.subscribeToState(() => {
        this.runWithTimeRange(timeRange);
      })
    );
    if (this.shouldRunQueriesOnActivate()) {
      this.runQueries();
    }
    return () => this._onDeactivate();
  }
  _handleDataLayers() {
    const dataLayers = sceneGraph.getDataLayers(this);
    const observables = [];
    const resultsMap = /* @__PURE__ */ new Map();
    if (dataLayers.length > 0) {
      dataLayers.forEach((layer) => {
        observables.push(layer.getResultsStream());
      });
      this._dataLayersSub = merge(observables).pipe(
        mergeAll(),
        map((v) => {
          resultsMap.set(v.origin.state.key, v.data);
          return resultsMap;
        })
      ).subscribe((result) => {
        this._onLayersReceived(result);
      });
    }
  }
  _onLayersReceived(results) {
    var _a;
    const timeRange = sceneGraph.getTimeRange(this);
    const dataLayers = sceneGraph.getDataLayers(this);
    const { dataLayerFilter } = this.state;
    let annotations = [];
    let alertStates = [];
    let alertState;
    const layerKeys = Array.from(results.keys());
    Array.from(results.values()).forEach((result, i) => {
      const layerKey = layerKeys[i];
      const layer = dataLayers.find((l) => l.state.key === layerKey);
      if (layer) {
        if (layer.topic === DataTopic.Annotations && result[DataTopic.Annotations]) {
          annotations = annotations.concat(result[DataTopic.Annotations]);
        }
        if (layer.topic === "alertStates") {
          alertStates = alertStates.concat(result.series);
        }
      }
    });
    if (dataLayerFilter == null ? void 0 : dataLayerFilter.panelId) {
      if (annotations.length > 0) {
        annotations = filterAnnotations(annotations, dataLayerFilter);
      }
      if (alertStates.length > 0) {
        for (const frame of alertStates) {
          const frameView = new DataFrameView(frame);
          for (const row of frameView) {
            if (row.panelId === dataLayerFilter.panelId) {
              alertState = row;
              break;
            }
          }
        }
      }
    }
    const baseStateUpdate = this.state.data ? this.state.data : __spreadProps(__spreadValues({}, emptyPanelData), { timeRange: timeRange.state.value });
    this.setState({
      data: __spreadProps(__spreadValues({}, baseStateUpdate), {
        annotations,
        alertState: alertState != null ? alertState : (_a = this.state.data) == null ? void 0 : _a.alertState
      })
    });
  }
  onVariableUpdatesCompleted(_variablesThatHaveChanged, dependencyChanged) {
    if (this.state._isWaitingForVariables) {
      this.runQueries();
      return;
    }
    if (dependencyChanged) {
      this.runQueries();
    }
  }
  shouldRunQueriesOnActivate() {
    if (!this.state.maxDataPoints && this.state.maxDataPointsFromWidth && !this._containerWidth) {
      return false;
    }
    if (this._variableValueRecorder.hasDependenciesChanged(this)) {
      writeSceneLog(
        "SceneQueryRunner",
        "Variable dependency changed while inactive, shouldRunQueriesOnActivate returns true"
      );
      return true;
    }
    if (!this.state.data) {
      return true;
    }
    if (this._isDataTimeRangeStale(this.state.data)) {
      return true;
    }
    return false;
  }
  _isDataTimeRangeStale(data) {
    const timeRange = sceneGraph.getTimeRange(this);
    const stateTimeRange = timeRange.state.value;
    const dataTimeRange = data.timeRange;
    if (stateTimeRange.from.unix() === dataTimeRange.from.unix() && stateTimeRange.to.unix() === dataTimeRange.to.unix()) {
      return false;
    }
    writeSceneLog("SceneQueryRunner", "Data time range is stale");
    return true;
  }
  _onDeactivate() {
    if (this._querySub) {
      this._querySub.unsubscribe();
      this._querySub = void 0;
    }
    if (this._dataLayersSub) {
      this._dataLayersSub.unsubscribe();
      this._dataLayersSub = void 0;
    }
    if (this._adhocFilterSub) {
      this._adhocFilterSub.unsubscribe();
    }
    this._variableValueRecorder.recordCurrentDependencyValuesForSceneObject(this);
  }
  setContainerWidth(width) {
    if (!this._containerWidth && width > 0) {
      this._containerWidth = width;
      if (this.state.maxDataPointsFromWidth && !this.state.maxDataPoints) {
        setTimeout(() => {
          if (this.isActive && !this.state._hasFetchedData) {
            this.runQueries();
          }
        }, 0);
      }
    } else {
      if (width > 0) {
        this._containerWidth = width;
      }
    }
  }
  isDataReadyToDisplay() {
    return Boolean(this.state._hasFetchedData);
  }
  runQueries() {
    const timeRange = sceneGraph.getTimeRange(this);
    this.runWithTimeRange(timeRange);
  }
  getMaxDataPoints() {
    var _a;
    if (this.state.maxDataPoints) {
      return this.state.maxDataPoints;
    }
    return this.state.maxDataPointsFromWidth ? (_a = this._containerWidth) != null ? _a : 500 : 500;
  }
  cancelQuery() {
    var _a;
    (_a = this._querySub) == null ? void 0 : _a.unsubscribe();
    if (this._dataLayersSub) {
      this._dataLayersSub.unsubscribe();
      this._dataLayersSub = void 0;
    }
    this.setState({
      data: __spreadProps(__spreadValues({}, this.state.data), { state: LoadingState.Done })
    });
  }
  async runWithTimeRange(timeRange) {
    var _a, _b;
    if (!this._dataLayersSub) {
      this._handleDataLayers();
    }
    const runRequest = getRunRequest();
    (_a = this._querySub) == null ? void 0 : _a.unsubscribe();
    if (sceneGraph.hasVariableDependencyInLoadingState(this)) {
      writeSceneLog("SceneQueryRunner", "Variable dependency is in loading state, skipping query execution");
      this.setState({ _isWaitingForVariables: true });
      return;
    }
    if (this.state._isWaitingForVariables) {
      this.setState({ _isWaitingForVariables: false });
    }
    const { queries } = this.state;
    if (!(queries == null ? void 0 : queries.length)) {
      this._setNoDataState();
      return;
    }
    try {
      const datasource = (_b = this.state.datasource) != null ? _b : findFirstDatasource(queries);
      const ds = await getDataSource(datasource, this._scopedVars);
      if (!this._adhocFilterSet) {
        this.findAndSubscribeToAdhocFilters(datasource == null ? void 0 : datasource.uid);
      }
      const [request, secondaryRequest] = this.prepareRequests(timeRange, ds);
      writeSceneLog("SceneQueryRunner", "Starting runRequest", this.state.key);
      if (secondaryRequest) {
        this._querySub = forkJoin([runRequest(ds, request), runRequest(ds, secondaryRequest)]).pipe(timeShiftQueryResponseOperator).subscribe(this.onDataReceived);
      } else {
        this._querySub = runRequest(ds, request).subscribe(this.onDataReceived);
      }
    } catch (err) {
      console.error("PanelQueryRunner Error", err);
      const result = __spreadProps(__spreadValues(__spreadValues({}, emptyPanelData), this.state.data), {
        state: LoadingState.Error,
        errors: [toDataQueryError(err)]
      });
      this.setState({
        data: result
      });
    }
  }
  _combineDataLayers(data) {
    if (this.state.data && this.state.data.annotations) {
      data.annotations = (data.annotations || []).concat(this.state.data.annotations);
    }
    if (this.state.data && this.state.data.alertState) {
      data.alertState = this.state.data.alertState;
    }
    return data;
  }
  _setNoDataState() {
    if (this.state.data !== emptyPanelData) {
      this.setState({ data: emptyPanelData });
    }
  }
  getTimeCompare() {
    if (!this.parent) {
      return null;
    }
    return getClosest(this.parent, (s) => {
      let found = null;
      if (s instanceof SceneTimeRangeCompare) {
        return s;
      }
      s.forEachChild((child) => {
        if (child instanceof SceneTimeRangeCompare) {
          found = child;
        }
      });
      return found;
    });
  }
  findAndSubscribeToAdhocFilters(uid) {
    var _a;
    const set = findActiveAdHocFilterSetByUid(uid);
    if (!set || set.state.applyMode !== "same-datasource") {
      return;
    }
    this._adhocFilterSet = set;
    this._adhocFilterSub = (_a = this._adhocFilterSet) == null ? void 0 : _a.subscribeToState(() => this.runQueries());
  }
}
function findFirstDatasource(targets) {
  var _a, _b;
  return (_b = (_a = targets.find((t) => t.datasource !== null)) == null ? void 0 : _a.datasource) != null ? _b : void 0;
}

export { SceneQueryRunner, findFirstDatasource, getNextRequestId };
//# sourceMappingURL=SceneQueryRunner.js.map
